import type { BenLanguageBackendCode } from '@ben/common/languages';
import Select from '@ben/orion/deprecated-select';
import Input, { InputVariant } from '@ben/orion/input';
import RadioButton from '@ben/orion/radio-button';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import {
  OptOutReasonGroupType,
  type IModularBenefitDetail,
  type IModularBenefitMessages,
} from '@ben/shared/types/columbia';
import { decodeHtmlEntities } from '@ben/shared/utils/decode-html-entities';
import { memo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { fieldCannotBeBlankErrorMessage } from '../../shared/constants';
import CategoriesSelect from '../categories/categories-select';
import ModelDropdown from '../model-select/model-dropdown';
import ModelMultiDropdown from '../model-select/model-multi-dropdown';
import OptInOutModal from '../opt-in-out-modal';
import ProgrammeDropdown from '../programme-select/programme-dropdown';
import Content from './content';
import CTALink, { CTALinkType } from './cta-link';
import TranslatableField from './translatable-field';

interface GeneralInformationProps {
  errors: Record<string, boolean>;
  warnings: IModularBenefitMessages;
  successes: IModularBenefitMessages;
  benefitTypes?: string[];
  isScheduledVersion?: boolean;
}

const DISCOUNT_ENROLMENT_TYPE_WARNING =
  'Note: Discount type benefits are info only benefits with the ability to redeem a discount code. Do not add pricing, steps, etc.';

const GeneralInformation = ({
  errors,
  warnings,
  successes,
  benefitTypes,
  isScheduledVersion,
}: GeneralInformationProps) => {
  const { control, setValue } = useFormContext<IModularBenefitDetail>();
  const [
    isActive,
    employer,
    externalLinkUrl,
    optOutReasonsGroupType,
    outboundProvider,
  ] = useWatch({
    control,
    name: [
      'active',
      'employer',
      'externalLinkUrl',
      'optOutReasonsGroupType',
      'outboundProvider',
      'allowanceImmediateDeduction',
    ],
    exact: true,
  });

  const optOutOptionsEndpoint =
    optOutReasonsGroupType === OptOutReasonGroupType.OPT_OUT
      ? 'opt-out-reasons-groups'
      : 'benefit-waiver-reasons-groups';
  const [ctaLinkType, setCTALinkType] = useState<CTALinkType>(
    outboundProvider ? CTALinkType.OutboundSSO : CTALinkType.Fixed
  );
  const isCTALabelEnabled =
    ctaLinkType === CTALinkType.OutboundSSO ||
    Boolean(externalLinkUrl) ||
    Boolean(outboundProvider);

  const getEnrolmentTypeWarning = ({
    enrolmentType,
    isDirty,
  }: {
    enrolmentType?: string;
    isDirty: boolean;
  }) => {
    const enrolmentTypeWarning = isDirty ? undefined : warnings.enrolmentType;
    const discountEnrolmentTypeWarning =
      enrolmentType === 'DISCOUNT'
        ? DISCOUNT_ENROLMENT_TYPE_WARNING
        : undefined;

    return [enrolmentTypeWarning, discountEnrolmentTypeWarning]
      .filter(Boolean)
      .join(', ');
  };

  return (
    <Wrapper>
      <TitleWrapper>
        <Typography variant={TypographyVariant.h2}>
          General Information
        </Typography>
        <Typography variant={TypographyVariant.bodyMd1}>
          All fields below are required, unless specified optional.
        </Typography>
      </TitleWrapper>
      <GeneralInformationContainer>
        <TwoColumnRow>
          <Controller
            name={'status'}
            control={control}
            rules={{ required: fieldCannotBeBlankErrorMessage }}
            render={({ field, fieldState }) => {
              const { value, onChange } = field;
              return (
                <Select
                  key={value}
                  name="Benefit status"
                  defaultValue={value ?? undefined}
                  onChange={(value) => {
                    setValue('active', value === 'PUBLISHED');
                    onChange(value);
                  }}
                  options={
                    isScheduledVersion
                      ? [{ key: 'SCHEDULED', label: 'Scheduled' }]
                      : [
                          { key: 'PUBLISHED', label: 'Published' },
                          { key: 'UNPUBLISHED', label: 'Unpublished' },
                          { key: 'DRAFT', label: 'Draft' },
                        ]
                  }
                  disabled={isScheduledVersion}
                  hasError={!!fieldState.error}
                  errorMessage={
                    fieldState.error?.message || fieldCannotBeBlankErrorMessage
                  }
                  width="100%"
                  data-testid="status-select"
                />
              );
            }}
          />
          <Controller
            name="newBenefitType"
            control={control}
            rules={{ required: fieldCannotBeBlankErrorMessage }}
            render={({ field, fieldState }) => {
              const { value, onChange } = field;
              return (
                <Select
                  key={value}
                  name="Benefit payroll logic type"
                  defaultValue={value}
                  onChange={onChange}
                  showSearch
                  options={
                    benefitTypes?.map((benefitType) => ({
                      key: benefitType,
                      label: benefitType,
                    })) ?? []
                  }
                  hasError={!!fieldState.error}
                  errorMessage={
                    fieldState.error?.message || fieldCannotBeBlankErrorMessage
                  }
                  width="100%"
                  data-testid="benefit-type-select"
                />
              );
            }}
          />
        </TwoColumnRow>
        <TwoColumnRow>
          <TranslatableField inputLabel={'Internal name'}>
            {(language: BenLanguageBackendCode) => (
              <Controller
                name={`name${language}`}
                control={control}
                render={({ field, fieldState: { error, isDirty } }) => {
                  const { value, ...rest } = field;
                  return (
                    <Input
                      {...rest}
                      value={decodeHtmlEntities(value || '')}
                      width="100%"
                      errorMessage={error?.message}
                      successMessage={
                        isDirty ? undefined : successes[`name${language}`]
                      }
                      hasError={!!error}
                      showLabel={false}
                    />
                  );
                }}
              />
            )}
          </TranslatableField>

          <TranslatableField
            inputLabel={'Display name'}
            hintText="Display name is the user facing name of the benefit. It can differ to the Benefit name."
          >
            {(language: BenLanguageBackendCode) => (
              <Controller
                name={`displayName${language}`}
                control={control}
                render={({ field, fieldState: { error, isDirty } }) => {
                  const { value, ...rest } = field;
                  return (
                    <Input
                      {...rest}
                      value={decodeHtmlEntities(value || '')}
                      width="100%"
                      errorMessage={error?.message}
                      successMessage={
                        isDirty
                          ? undefined
                          : successes[`displayName${language}`]
                      }
                      hasError={!!error}
                      showLabel={false}
                    />
                  );
                }}
              />
            )}
          </TranslatableField>
        </TwoColumnRow>
        <TranslatableField
          inputLabel={'Short Description (optional)'}
          hintText="Will appear in the benefit selection tile"
        >
          {(language: BenLanguageBackendCode) => (
            <Controller
              name={`shortDescription${language}`}
              control={control}
              render={({ field }) => {
                const { value, ...rest } = field;
                return (
                  <Input
                    {...rest}
                    variant={InputVariant.TextArea}
                    value={value ?? ''}
                    width="100%"
                    maxLength={70}
                    height="50px"
                    showLabel={false}
                  />
                );
              }}
            />
          )}
        </TranslatableField>
        <TwoColumnRow>
          <Controller
            name="enrolmentType"
            control={control}
            rules={{ required: fieldCannotBeBlankErrorMessage }}
            render={({ field, fieldState: { isDirty, error } }) => {
              const { value, onChange } = field;
              return (
                <Select
                  key={value}
                  name="enrolment type"
                  defaultValue={value}
                  onChange={onChange}
                  options={[
                    { key: 'OPT_IN', label: 'Opt In' },
                    { key: 'OPT_OUT', label: 'Opt Out' },
                    { key: 'LOCKED', label: 'Locked' },
                    { key: 'INFO_ONLY', label: 'Info only' },
                    { key: 'DISCOUNT', label: 'Discount' },
                  ]}
                  hasError={!!error}
                  errorMessage={
                    typeof error?.message === 'string'
                      ? error?.message
                      : fieldCannotBeBlankErrorMessage
                  }
                  warningMessage={getEnrolmentTypeWarning({
                    isDirty,
                    enrolmentType: value,
                  })}
                  successMessage={isDirty ? undefined : successes.enrolmentType}
                  width="100%"
                  data-testid="enrolment-type-select"
                />
              );
            }}
          />
          <Controller
            name={'employer'}
            control={control}
            rules={{ required: fieldCannotBeBlankErrorMessage }}
            render={({ field, fieldState: { isDirty, error } }) => {
              const { value, onChange } = field;
              return (
                <ModelDropdown
                  key={value}
                  name="Employer"
                  apiName="employers"
                  selected={Number(value)}
                  onChange={onChange}
                  hasError={!!error}
                  errorMessage={
                    typeof error?.message === 'string'
                      ? error?.message
                      : fieldCannotBeBlankErrorMessage
                  }
                  warningMessage={isDirty ? undefined : warnings.employer}
                  successMessage={isDirty ? undefined : successes.employer}
                  width="100%"
                  disabled={isScheduledVersion}
                />
              );
            }}
          />
        </TwoColumnRow>
        <TwoColumnRow>
          <Controller
            name={'benefitProgramme'}
            control={control}
            rules={{
              required: isActive ? fieldCannotBeBlankErrorMessage : false,
            }}
            render={({ field, fieldState }) => {
              const { value, ...rest } = field;
              return (
                <ProgrammeDropdown
                  {...rest}
                  key={value}
                  selected={Number(value)}
                  isActive={isActive}
                  hasError={!!fieldState.error}
                  errorMessage={
                    fieldState.error?.message || fieldCannotBeBlankErrorMessage
                  }
                  employer={employer?.toString()}
                  disabled={isScheduledVersion}
                />
              );
            }}
          />
          <Controller
            name={'benefitRequirements'}
            control={control}
            render={({ field, fieldState: { isDirty } }) => {
              const { value, ...rest } = field;
              return (
                <ModelMultiDropdown
                  key={value?.[0]}
                  {...rest}
                  name="Benefit requirements (optional)"
                  defaultValue={value ?? []}
                  apiName="benefit-requirements"
                  width="376px"
                  warningMessage={
                    isDirty ? undefined : warnings.benefitRequirements
                  }
                  successMessage={
                    isDirty ? undefined : successes.benefitRequirements
                  }
                />
              );
            }}
          />
        </TwoColumnRow>
        <TwoColumnRow>
          <Controller
            name={'categories'}
            control={control}
            render={({ field }) => {
              const { value, ...rest } = field;
              return (
                <CategoriesSelect
                  key={value?.[0]?.id}
                  {...rest}
                  employer={Number(employer)}
                  defaultValue={value?.map(({ id }) => String(id)) ?? []}
                  width="376px"
                />
              );
            }}
          />
          <Controller
            name="optInButtonText"
            control={control}
            rules={{
              required: false,
            }}
            render={({ field, fieldState }) => {
              const { value, ...rest } = field;
              return (
                <Input
                  {...rest}
                  hasError={!!fieldState.error || !!errors?.['optInButtonText']}
                  errorMessage={fieldState.error?.message}
                  name="Opt in button text (optional)"
                  hintText={`Overwrite the "Opt into benefit" button text. Note: this will not be translated and will not change the action of the button.`}
                  value={value ?? ''}
                  width="100%"
                />
              );
            }}
          />
        </TwoColumnRow>
        <CTALink
          errors={errors}
          successes={successes}
          type={ctaLinkType}
          onTypeChange={setCTALinkType}
        />
        <TranslatableField inputLabel={'CTA label'}>
          {(language: BenLanguageBackendCode) => (
            <Controller
              control={control}
              name={`ctaText${language}`}
              rules={{
                required:
                  isCTALabelEnabled && language === 'En'
                    ? fieldCannotBeBlankErrorMessage
                    : false,
              }}
              render={({ field, fieldState }) => {
                const { value, ...rest } = field;
                return (
                  <Input
                    {...rest}
                    hasError={
                      !!fieldState.error || !!errors?.[`ctaText${language}`]
                    }
                    errorMessage={
                      fieldState.error?.message ||
                      fieldCannotBeBlankErrorMessage
                    }
                    value={isCTALabelEnabled ? value : ''}
                    width="100%"
                    disabled={!isCTALabelEnabled}
                    showLabel={false}
                  />
                );
              }}
            />
          )}
        </TranslatableField>

        <TranslatableField
          inputLabel={isActive ? 'Overview' : 'Overview (optional)'}
          hintText='To embed a video, use the following format: <iframe width="480" height="270" src="REPLACE WITH VIDEO URL" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>'
        >
          {(language: BenLanguageBackendCode) => (
            <Content language={language} errors={errors} />
          )}
        </TranslatableField>

        <RadioButtonWrapper>
          <Controller
            name="optOutReasonsGroupType"
            control={control}
            render={({ field: { value, ...rest } }) => (
              <RadioButton
                value={OptOutReasonGroupType.WAIVER}
                checked={value === OptOutReasonGroupType.WAIVER}
                label="Opt-out waiver reason"
                showLabel
                {...rest}
              />
            )}
          />
          <Controller
            name="optOutReasonsGroupType"
            control={control}
            render={({ field: { value, ...rest } }) => (
              <RadioButton
                value={OptOutReasonGroupType.OPT_OUT}
                checked={value === OptOutReasonGroupType.OPT_OUT}
                label="Requires opt-out reason"
                showLabel
                {...rest}
              />
            )}
          />
        </RadioButtonWrapper>

        {!!optOutReasonsGroupType && (
          <Controller
            name="optOutReasonsGroup"
            control={control}
            render={({ field }) => {
              const { value, ...rest } = field;
              return (
                <ModelDropdown
                  key={value}
                  {...rest}
                  name="Select opt-out group (optional)"
                  apiName={optOutOptionsEndpoint}
                  selected={value}
                  width="100%"
                  clearable
                />
              );
            }}
          />
        )}
      </GeneralInformationContainer>
      <OptInOutModal />
    </Wrapper>
  );
};

export default memo(GeneralInformation);

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
    border: 1px solid ${theme.palette.neutral[60]};
    padding: ${theme.spacing(3)};
    border-radius: 10px;
  `}
`;

const TitleWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `}
`;

const GeneralInformationContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
    background: ${theme.palette.primary.bg};
    padding: ${theme.spacing(3)};
    border-radius: 10px;
    width: 100%;
  `}
`;

const TwoColumnRow = styled.div`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${theme.spacing(2)};
    width: 100%;
  `}
`;

const RadioButtonWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: ${theme.spacing(2)};
  `}
`;
